import { setDateTimeLocalization } from '@racemap/utilities/functions/datetime';
import { ConfigProvider } from 'antd';
import { enableMapSet } from 'immer';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { guardPageTranslation } from 'src/lib/guardPageTranslation';
import { antdConfig } from 'src/lib/theme';
import { prefetchMap } from './components/Player/Map/loadMapStyle';
import { PlayerRouter } from './router/player.router';

import 'normalize.css/normalize.css';

import '@css/player.scss';
import '@css/not-activated.css';
import 'mapbox-gl/dist/mapbox-gl.css';

import WarningSVG from '@assets/player/warning.svg';
import { RacemapAPIClient } from '@racemap/utilities/api-client';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { AuthorizationStates } from '@racemap/utilities/consts/events';
import { isEmptyString } from '@racemap/utilities/functions/utils';
import screenfull from 'screenfull';
import { installChunkFailureRecovery, startBuildUpdateWatch } from 'src/lib/buildUpdate';
import { trackEvent } from 'src/lib/googleAnalytics';
import { parseHash } from 'src/lib/player/controller/HashManager';
import { getFavoritesToRestore } from 'src/lib/player/favorites';
import { t } from 'src/lib/player/i18n';
import { loadData, waitForMap } from 'src/lib/player/model';
import { getPositionToRestore } from 'src/lib/player/playbackPosition';
import { getCurrentEvents, reduceEndTime } from 'src/lib/player/selectors';
import { useStore } from 'src/lib/player/store';
import { detectIE, detectWebGL, sleep } from 'src/lib/player/utils';
import { initClock } from 'src/lib/player/utils/clock';

const CDN_HOST = import.meta.env.VITE_CDN_HOST;
const apiClient = new RacemapAPIClient(CDN_HOST, { includeCredentials: false });

setDateTimeLocalization();

// makes the use of Map and Set in immer.js possible
enableMapSet();
guardPageTranslation();

// Passive surface, frequently embedded in an organiser's page: reload without asking. Costs
// the viewer their scrub position and selection, which is acceptable once per deploy.
startBuildUpdateWatch({ auto: true, host: CDN_HOST });
installChunkFailureRecovery();

/* Window resize handler (for sizing in Android app) */
function handleWindowResize() {
  for (const el of Array.from(document.querySelectorAll('html, body'))) {
    (el as HTMLElement).style.width = `${window.innerWidth}px`;
    (el as HTMLElement).style.height = `${window.innerHeight}px`;
  }
}

window.addEventListener('resize', handleWindowResize, false);
handleWindowResize();

/* Feature detection */
trackEvent('capability', { feature: 'geolocation', supported: 'geolocation' in window.navigator });
trackEvent('capability', { feature: 'fullscreen', supported: screenfull?.isEnabled === true });
trackEvent('capability', { feature: 'webgl', supported: detectWebGL() });

const IEversion = detectIE();
if (IEversion !== false && IEversion < 11) {
  alert(t('Old IE'));
}

async function init() {
  const el = document.querySelector('.racemap');
  if (el == null) return;
  const overlay = el.querySelector('.overlay');
  if (overlay == null) return;
  const loadingIndicator = overlay.querySelector('.loading-indicator');
  if (loadingIndicator == null) return;

  try {
    // search for event id or slug in URL path
    // http(s)://<host>/player/<eventIdOrSlug>
    const location = window.location.pathname;
    const eventIdOrSlug = location.match(/\/player\/([^/]+)/)?.[1];
    if (eventIdOrSlug == null || isEmptyString(eventIdOrSlug)) {
      throw new Error('No event ID or slug provided in URL');
    }

    // Calibrates in parallel with the downloads; loadData waits for it before parsing
    const clock = initClock();
    // Load basic informations of event with slug or id
    const options = await apiClient.getPlayerBaseOptions(eventIdOrSlug);
    const eventId = options.eventId;

    // Support logs for faster navigation - Please keep
    console.log('Event ID or Slug from URL:', eventIdOrSlug);
    console.log('Config:', `${window.location.origin}/admin/events/${eventId}/visualisation`);

    // If event not authorized, show error
    if (options.authorization === AuthorizationStates.NONE) {
      const div = document.createElement('div');
      div.className = 'not-activated';
      document.body.appendChild(div);
    }

    const hashState = parseHash(window.location.hash);

    // ponytail: zeroing the var collapses every layout rule that reserves room for the control bar
    if (hashState.mapOnly === true) {
      document.documentElement.style.setProperty('--control-bar-height', '0px');
    }

    // Set base color from URL hash parameter or options
    const hashColorMatch = window.location.hash.match(/color=([A-Za-z0-9]{6})/)?.[1];
    if (hashColorMatch != null) {
      document.documentElement.style.setProperty('--base-color', `#${hashColorMatch}`);
    } else if (options.baseColor != null) {
      document.documentElement.style.setProperty('--base-color', options.baseColor);
    } else {
      document.documentElement.style.setProperty('--base-color', RacemapColors.PaleBlue);
    }

    useStore.getState().actions.receiveOptions(options, hashState);
    prefetchMap(useStore.getState().options.mapType);
    // Points wait for the map so they do not compete with its tiles for bandwidth
    const events = await loadData(eventId, undefined, {
      beforeParse: clock,
      beforePoints: waitForMap,
    });

    useStore.getState().actions.receiveBaseData(events, hashState.events ?? [], hashState);

    // Has to happen before the first render, so that HashManager already
    // sees the restored selection and writes it back into the hash.
    const favorites = getFavoritesToRestore(hashState, eventId);
    if (favorites != null) useStore.getState().actions.setSelected(favorites);

    // Restore where the viewer left off. Also before the first render, so the player does not
    // visibly jump from the default position to the restored one.
    const state = useStore.getState();
    const endTime = reduceEndTime(getCurrentEvents(state));
    const position = getPositionToRestore(hashState, eventId, endTime);
    if (position != null) state.actions.setTimerTime(position);

    const container = el.querySelector('.container');
    if (container == null) throw new Error('Could not find root element');

    const root = createRoot(container);
    root.render(
      <StrictMode>
        <ConfigProvider {...antdConfig}>
          <PlayerRouter />
        </ConfigProvider>
      </StrictMode>,
    );

    overlay.className += ' removed';
    await sleep(300);
    if (overlay.parentElement != null) overlay.parentElement.removeChild(overlay);
    if (import.meta.env.PROD) useStore.getState().actions.resumeTimer();
  } catch (err) {
    loadingIndicator.innerHTML = `<img src="${WarningSVG}" alt="Warning" style="width: 128px; height: 128px;" />`;
    console.error(err);
  }
}
init();
